import { FC, useCallback, useEffect, useState } from 'react';
import { Box, Link, IconButton } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import BugReportIcon from '@mui/icons-material/BugReport';
import DeleteIcon from '@mui/icons-material/Delete';
import UtmsButton from '@shared/components/ui/UtmsButton';
import { useTranslation } from 'react-i18next';
import { Defect } from '../types';
import { getDefectLinkById } from '@shared/utils/utils';
import DefectFormDialog from './DefectFormDialog';
import DefectSelector from './DefectSelector';
import UtmsChip from '@shared/components/ui/UtmsChip';
import { DEFECT_STATUS_COLOR, DEFECT_TYPE_COLOR } from '../constants';
import { useSelector } from 'react-redux';
import { selectAddDefectError, selectAddDefectStatus, selectCreatedDefect } from '../store/selectors';
import { RequestStatusEnum } from '@shared/types';
import ModalErrorContainer from '@shared/components/modals/ModalErrorContainer';
import ModalSuccessContainer from '@shared/components/modals/ModalSuccessContainer';
import { addDefectReset } from '../store';
import ComponentWithChunks from '@shared/components/others/ComponentWithChunk/ComponentWithChunk';
import { useDefectsChunk } from '../hooks/useDefectsChunk';
import { convertToTranslationKey, toTitleCase } from '@shared/utils/utilsOld';
import { usePrevious } from '@shared/hooks/usePrevious';

interface DefectsListManagerProps {
    defects: Defect[];
    onAdd: (defect: Defect) => void;
    onRemove: (defectId: Defect['pk']) => () => void;
    showList?: boolean;
    onSelect?: (defect: Defect | null) => void;
    listMaxHeight?: number;
}

const DefectsListManager = (props: DefectsListManagerProps) => (
    <ComponentWithChunks
        featuresArray={['defects']}
        hooksArray={[useDefectsChunk]}
        Component={DefectsListManagerComponent}
        {...props}
    />
);

const DefectsListManagerComponent: FC<DefectsListManagerProps> = ({
    defects,
    onAdd,
    onRemove,
    showList,
    onSelect,
    listMaxHeight = 400,
}) => {
    const { t } = useTranslation();
    const [openCreateDialog, setOpenCreateDialog] = useState(false);
    const [selectedDefect, setSelectedDefect] = useState<Defect | null>(null);
    const isDefectAlreadyAdded = Boolean(selectedDefect && defects.some((d) => d.pk === selectedDefect.pk));
    const addDefectStatus = useSelector(selectAddDefectStatus);
    const createdDefect = useSelector(selectCreatedDefect);
    const prevAddDefectStatus = usePrevious(addDefectStatus);

    const handleChangeSelectedDefect = useCallback(
        (defect: Defect | null) => {
            setSelectedDefect(defect);
            onSelect?.(defect);
        },
        [onSelect],
    );

    useEffect(() => {
        if (addDefectStatus === RequestStatusEnum.Success && prevAddDefectStatus !== addDefectStatus && createdDefect) {
            showList ? onAdd(createdDefect) : handleChangeSelectedDefect(createdDefect);
        }
    }, [addDefectStatus, createdDefect, handleChangeSelectedDefect, onAdd, prevAddDefectStatus, showList]);

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {showList && (
                <>
                    <Box sx={{ overflowY: 'auto', maxHeight: listMaxHeight }}>
                        {defects.map((defect) => (
                            <Box
                                key={defect.pk}
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                }}
                            >
                                <Box sx={{ display: 'flex', gap: 2, alignItems: 'baseline' }}>
                                    <UtmsChip
                                        label={t(defect.status.toLowerCase())}
                                        color={DEFECT_STATUS_COLOR[defect.status]}
                                    />
                                    <UtmsChip
                                        label={toTitleCase(t(convertToTranslationKey(defect.type)))}
                                        color={DEFECT_TYPE_COLOR[defect.type]}
                                    />
                                    <Box sx={{ display: 'flex', gap: 1 }}>
                                        #{defect.pk}
                                        {defect.jiraIssueKey && (
                                            <Link
                                                href={defect.jiraIssueUrl}
                                                underline="hover"
                                                target="_blank"
                                                sx={{ cursor: 'pointer' }}
                                            >
                                                {defect.jiraIssueKey}
                                            </Link>
                                        )}
                                        <Link
                                            underline="hover"
                                            target="_blank"
                                            sx={{ cursor: 'pointer' }}
                                            href={getDefectLinkById(defect.pk)}
                                        >
                                            {defect.name}
                                        </Link>
                                    </Box>
                                </Box>

                                <IconButton color="error" onClick={onRemove(defect.pk)}>
                                    <DeleteIcon />
                                </IconButton>
                            </Box>
                        ))}
                    </Box>
                </>
            )}
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                <DefectSelector
                    value={selectedDefect}
                    onChange={handleChangeSelectedDefect}
                    error={isDefectAlreadyAdded}
                    helperText={isDefectAlreadyAdded ? t('defect_already_linked') : undefined}
                />
                {showList && (
                    <UtmsButton
                        startIcon={<AddIcon />}
                        disabled={!selectedDefect || isDefectAlreadyAdded}
                        onClick={() => {
                            selectedDefect && onAdd(selectedDefect);
                            setSelectedDefect(null);
                        }}
                    >
                        {t('add')}
                    </UtmsButton>
                )}
            </Box>
            <Box>
                <UtmsButton startIcon={<BugReportIcon />} onClick={() => setOpenCreateDialog(true)}>
                    {t('create')} {t('new')} {t('defect').toLowerCase()}
                </UtmsButton>
            </Box>
            <DefectFormDialog open={openCreateDialog} onClose={() => setOpenCreateDialog(false)} mode="create" />
            {addDefectStatus === RequestStatusEnum.Error && (
                <ModalErrorContainer
                    object={t('defect_r')}
                    action={t('adding')}
                    resetActionCallback={addDefectReset}
                    errorSelector={selectAddDefectError}
                />
            )}
            {addDefectStatus === RequestStatusEnum.Success && (
                <ModalSuccessContainer object={t('defect')} action={t('added')} resetActionCallback={addDefectReset} />
            )}
        </Box>
    );
};

export default DefectsListManager;
